import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setSignupData,
  setError,
  setVerificationStep,
} from "../../store/slices/signupSlice";
import { authService, searchService } from "../../services/api";
import { useDebounce } from "../../hooks/useDebounce";

const CoachSkeleton = () => (
  <div className="space-y-3">
    {[1, 2, 3, 4].map((i) => (
      <div key={i} className="flex items-center gap-3 md:gap-4 p-3 md:p-4 border border-gray-100 rounded-xl bg-white shadow-sm animate-pulse">
        <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-gray-200 flex-shrink-0" />
        <div className="flex-1 space-y-2 min-w-0">
          <div className="flex justify-between items-start gap-2">
            <div className="h-4 md:h-5 bg-gray-200 rounded w-2/3" />
            <div className="h-6 md:h-7 bg-gray-200 rounded-lg w-16 md:w-20 flex-shrink-0" />
          </div>
          <div className="flex flex-wrap gap-2 md:gap-3">
            <div className="h-3 md:h-4 bg-gray-200 rounded w-10 md:w-12" />
            <div className="h-3 md:h-4 bg-gray-200 rounded w-16 md:w-20" />
            <div className="h-3 md:h-4 bg-gray-200 rounded w-12 md:w-16" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

const CoachDetailsStep = () => {
  const dispatch = useDispatch();
  const { error } = useSelector((state) => state.signup);
  const [loading, setLoading] = useState(false);

  // Search states
  const [coachSearchQuery, setCoachSearchQuery] = useState("");
  const [coachSearchResults, setCoachSearchResults] = useState([]);
  const [searchPagination, setSearchPagination] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showSearchModal, setShowSearchModal] = useState(false);

  const [selectedCoach, setSelectedCoach] = useState(null);
  const searchInputRef = useRef(null);
  const abortControllerRef = useRef(null);
  const debouncedSearchQuery = useDebounce(coachSearchQuery, 300);

  const [formData, setFormData] = useState({
    name: "",
    licenseNumber: "",
    thesports_coach_id: "",
  });

  // Search coaches when query changes
  useEffect(() => {
    if (debouncedSearchQuery.trim() && showSearchModal) {
      searchCoaches(debouncedSearchQuery, 1);
      setCurrentPage(1);
    } else if (!debouncedSearchQuery.trim()) {
      // Abort any pending request when clearing search
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      setCoachSearchResults([]);
      setSearchPagination(null);
      setIsSearching(false);
    }
    
    // Cleanup: abort request on unmount or when modal closes
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [debouncedSearchQuery, showSearchModal]);

  const searchCoaches = async (query, page = 1) => {
    // Abort previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    setIsSearching(true);
    try {
      const params = {
        type: "coaches",
        query: query,
        per_page: 10,
        page: page,
      };

      const response = await searchService.search(params, abortController.signal);

      // Check if request was aborted
      if (abortController.signal.aborted) {
        return;
      }

      if (response.success && response.data) {
        const coaches = response.data.results?.coaches || [];
        setCoachSearchResults(coaches.slice(0, 15));
        setSearchPagination(response.pagination || null);
      } else {
        setCoachSearchResults([]);
        setSearchPagination(null);
      }
    } catch (error) {
      // Ignore abort errors
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        return;
      }
      console.error("Coach search error:", error);
      // Only update state if request wasn't aborted
      if (!abortController.signal.aborted) {
        setCoachSearchResults([]);
        setSearchPagination(null);
      }
    } finally {
      // Only update loading state if this is still the current request
      if (abortControllerRef.current === abortController) {
        setIsSearching(false);
        abortControllerRef.current = null;
      }
    }
  };

  const handlePageChange = (page) => {
    if (coachSearchQuery.trim()) {
      setCurrentPage(page);
      searchCoaches(coachSearchQuery, page);
    }
  };

  const handleCoachSelect = (coach) => {
    // Abort any pending search when selecting a coach
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    setSelectedCoach(coach);
    setFormData((prev) => ({
      ...prev,
      name: coach.name,
      thesports_coach_id: coach.id,
    }));
    setShowSearchModal(false);
    setCoachSearchQuery("");
    setCoachSearchResults([]);
    setSearchPagination(null);
  };

  const handleOpenSearch = () => {
    setShowSearchModal(true);
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);
  };

  const handleBack = () => {
    dispatch(setVerificationStep("type"));
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.licenseNumber || !formData.thesports_coach_id) {
      dispatch(setError("Please select a coach from search and enter license number"));
      return;
    }

    setLoading(true);
    dispatch(setError(null));

    const coachData = {
      name: formData.name,
      license_number: formData.licenseNumber,
      thesports_coach_id: formData.thesports_coach_id,
    };

    try {
      const response = await authService.createCoach(coachData);

      if (response.success) {
        dispatch(setSignupData({ coachDetails: coachData }));
        dispatch(setVerificationStep("complete"));
      } else {
        dispatch(
          setError(response.error || "Failed to create coach profile")
        );
      }
    } catch (error) {
      console.error("Submit error:", error);
      dispatch(setError("An unexpected error occurred"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex items-center mb-6">
        <button
          onClick={handleBack}
          className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          aria-label="Go back to account type selection"
          tabIndex="0"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-5 h-5 mr-2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.75 19.5L8.25 12l7.5-7.5"
            />
          </svg>
          Back
        </button>
      </div>

      <div className="w-full max-w-lg mx-auto">
        <div className="flex items-center justify-center w-10 h-10 bg-[#374DF5] rounded-lg mb-4 shadow-sm shadow-blue-200">
          <svg
            width="14"
            height="13"
            viewBox="0 0 14 13"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9.26144 11.5897V10.4588C9.26144 9.85886 9.02313 9.28353 8.59894 8.85934C8.17475 8.43515 7.59943 8.19685 6.99953 8.19685H3.0412C2.44131 8.19685 1.86598 8.43515 1.44179 8.85934C1.0176 9.28353 0.779297 9.85886 0.779297 10.4588V11.5897M11.5233 4.23851V7.63137M13.2198 5.93494H9.82692M7.28227 3.67304C7.28227 4.92225 6.26958 5.93494 5.02037 5.93494C3.77115 5.93494 2.75846 4.92225 2.75846 3.67304C2.75846 2.42382 3.77115 1.41113 5.02037 1.41113C6.26958 1.41113 7.28227 2.42382 7.28227 3.67304Z"
              stroke="white"
              strokeWidth="1.32577"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h2 className="text-2xl leading-[28px] font-bold text-[#111927] mb-2">
          Coach Details
        </h2>
        <p className="text-[14px] leading-[20px] text-[#6C737F] mb-8">
          Search for your profile and provide your coaching license
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6 animate-in fade-in slide-in-from-top-2 duration-300">
            {error}
          </div>
        )}

        {/* Coach Selection */}
        <div className="space-y-5">
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-[#111927]">
              Search your profile
            </label>

            <button
              type="button"
              onClick={handleOpenSearch}
              className="w-full border border-gray-300 rounded-lg h-12 flex items-center bg-white shadow-sm hover:border-blue-400 transition-all group"
            >
              {selectedCoach ? (
                <div className="flex items-center justify-between w-full px-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 rounded-full bg-gray-100 bg-cover bg-center border border-gray-200"
                      style={{
                        backgroundImage: selectedCoach.logo ? `url(${selectedCoach.logo})` : "none",
                      }}
                    >
                      {!selectedCoach.logo && (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col text-left">
                      <span className="text-sm text-[#111927] font-semibold">
                        {selectedCoach.name}
                      </span>
                      {selectedCoach.team_name && (
                        <span className="text-[11px] text-[#6C737F]">
                          {selectedCoach.team_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="p-1.5 bg-gray-50 rounded-full group-hover:bg-blue-50 transition-colors">
                    <svg className="w-4 h-4 text-gray-500 group-hover:text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </div>
                </div>
              ) : (
                <div className="w-full px-4 flex items-center justify-between">
                  <span className="text-sm text-gray-400">Enter your name to search...</span>
                  <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              )}
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-[#111927] mb-1.5">
                License Number
              </label>
              <input
                type="text"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 shadow-sm transition-all"
                placeholder="Ex: UEFA-PRO-12345"
                value={formData.licenseNumber}
                onChange={(e) => handleInputChange("licenseNumber", e.target.value)}
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading || !formData.thesports_coach_id}
              className="w-full bg-[#2283FF] text-white py-3.5 px-4 rounded-lg font-bold hover:bg-[#1a6cd4] transition-all transform active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-md shadow-blue-100"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin -ml-1 mr-3 h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                  Creating Profile...
                </div>
              ) : (
                "Continue"
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Coach Search Modal */}
      {showSearchModal && (
        <div className="fixed inset-0 bg-[#111927]/60 backdrop-blur-[2px] flex items-center justify-center z-[100] transition-all p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl animate-in fade-in zoom-in duration-200 overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-5 md:p-6 border-b border-gray-100">
              <div>
                <h2 className="text-xl font-bold text-[#111927]">
                  Search Coach Profiles
                </h2>
                <p className="text-sm text-[#6C737F]">Find your professional profile in our database</p>
              </div>
              <button
                type="button"
                onClick={() => setShowSearchModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors text-[#6C737F] hover:text-[#111927]"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Search Input Section */}
            <div className="p-5 md:p-6 bg-[#F8F9FA]">
              <div className="relative group">
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="thomas"
                  value={coachSearchQuery}
                  onChange={(e) => setCoachSearchQuery(e.target.value)}
                  className="w-full h-14 pl-12 pr-4 border-2 border-blue-500 rounded-xl text-lg placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-blue-500/10 transition-all bg-white font-medium"
                />
                <svg
                  className="w-6 h-6 text-blue-500 absolute left-4 top-1/2 -translate-y-1/2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-4 md:p-5 lg:p-6 bg-white custom-scrollbar min-h-[200px]">
              {isSearching ? (
                <CoachSkeleton />
              ) : coachSearchResults.length > 0 ? (
                <div className="space-y-4">
                  {coachSearchResults.map((coach) => (
                    <button
                      key={coach.id}
                      type="button"
                      onClick={() => handleCoachSelect(coach)}
                      className="w-full p-3 md:p-4 border border-gray-100 rounded-xl hover:bg-blue-50/40 hover:border-blue-200 transition-all text-left flex items-start sm:items-center gap-3 md:gap-4 group relative shadow-sm hover:shadow-md"
                    >
                      <div
                        className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-gray-50 bg-cover bg-center border-2 border-white shadow-sm flex-shrink-0 group-hover:scale-105 transition-transform"
                        style={{
                          backgroundImage: coach.logo ? `url(${coach.logo})` : "none",
                        }}
                      >
                        {!coach.logo && (
                          <div className="w-full h-full flex items-center justify-center text-gray-300">
                            <svg className="w-6 h-6 md:w-8 md:h-8" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-1.5">
                          <h3 className="text-sm md:text-base font-bold text-[#111927] truncate group-hover:text-blue-600 transition-colors">
                            {coach.name || '-'}
                          </h3>
                          <span className="text-xs md:text-sm text-[#6C737F] font-medium whitespace-nowrap flex-shrink-0">
                            {coach.age && coach.age > 0 ? `${coach.age} years` : '-'}
                          </span>
                        </div>

                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] md:text-xs text-[#6C737F]">
                          <span className="font-bold text-[#111927] uppercase tracking-wider">
                            {coach.nationality || '-'}
                          </span>
                          
                          {coach.team_name && (
                            <>
                              <span className="text-gray-300">•</span>
                              <span className="font-medium text-[#6C737F] truncate max-w-[120px] md:max-w-none">
                                {coach.team_name}
                              </span>
                            </>
                          )}

                          {coach.preferred_formation && (
                            <>
                              <span className="text-gray-300">•</span>
                              <span className="italic font-medium text-gray-400">
                                {coach.preferred_formation}
                              </span>
                            </>
                          )}
                          
                          {!coach.team_name && !coach.preferred_formation && (
                            <>
                              <span className="text-gray-300">•</span>
                              <span className="text-gray-400">-</span>
                            </>
                          )}
                        </div>
                      </div>
                      
                      <div className="opacity-0 group-hover:opacity-100 transition-all scale-75 group-hover:scale-100">
                        <div className="bg-blue-600 text-white p-2 rounded-full shadow-lg">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                          </svg>
                        </div>
                      </div>
                    </button>
                  ))}

                  {/* Pagination Section */}
                  {searchPagination && (searchPagination.total_pages > 1 || searchPagination.has_more) && (
                    <div className="pt-6 border-t border-gray-100 mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                      <span className="text-sm font-medium text-[#6C737F]">
                        Page {currentPage} of {searchPagination.total_pages || '?'}
                      </span>
                      <div className="flex items-center gap-2 w-full sm:w-auto">
                        <button
                          type="button"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage <= 1}
                          className="flex-1 sm:flex-none px-5 py-2.5 border border-gray-200 rounded-xl text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors text-[#111927]"
                        >
                          Previous
                        </button>
                        <button
                          type="button"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={!searchPagination.has_more && currentPage >= searchPagination.total_pages}
                          className="flex-1 sm:flex-none px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-blue-700 transition-all shadow-sm shadow-blue-100"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : coachSearchQuery.trim() && !isSearching ? (
                <div className="flex flex-col items-center justify-center py-12 text-center h-full">
                  <div className="bg-gray-50 p-6 rounded-full mb-4">
                    <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 9.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold text-[#111927] mb-1">No results found</h3>
                  <p className="text-[#6C737F] max-w-xs text-sm">We couldn't find any coach matching "{coachSearchQuery}". Try a different spelling or name.</p>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center h-full">
                  <div className="bg-blue-50 p-6 rounded-full mb-4">
                    <svg className="w-12 h-12 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold text-[#111927] mb-1">Search Coach Profiles</h3>
                  <p className="text-[#6C737F] max-w-xs text-sm">Start typing your name to find your profile in our professional database</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #E5E7EB;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #D1D5DB;
        }
      `}</style>
    </>
  );
};

export default CoachDetailsStep;
